import i18n from "i18next";

import { initReactI18next } from "react-i18next";

import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";

import translationEN from "./locales/en.json";
import ConfigService from "./services/ConfigService";
import cdnClient from "./api/cdnClient";

const supportedLngs = [
  "az",
  "bg",
  "bn",
  "bs",
  "cnr",
  "de",
  "el",
  "en",
  "es",
  "fi",
  "fil",
  "fr",
  "hi",
  "hu",
  "id",
  "it",
  "ja",
  "ka",
  "km",
  "ko",
  "lo",
  "ms",
  "my",
  "nb",
  "nl",
  "pl",
  "pt-BR",
  "pt",
  "ru",
  "sr",
  "sw",
  "th",
  "tl",
  "tr",
  "uk",
  "uz",
  "vi",
  "zh",
];

const languageAliases: Record<string, string> = { ba: "bs", me: "cnr", no: "nb" };
const resolveLanguage = (lang: string): string => languageAliases[lang] || lang;

const fetchedLanguages = new Set();

const fetchRemoteTranslationFiles = async (currentLanguage: string) => {
  const resolvedLanguage = resolveLanguage(currentLanguage);
  const promoId = ConfigService.getPromoId();
  if (!promoId || fetchedLanguages.has(`${promoId}:${resolvedLanguage}`)) return;

  try {
    const response = await cdnClient.get(`/promo/${promoId}/${resolvedLanguage}.json`, {
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

    i18n.addResourceBundle(resolvedLanguage, "translation", response.data);
    fetchedLanguages.add(`${promoId}:${resolvedLanguage}`);
  } catch (err) {
    if (resolvedLanguage !== "en" && !fetchedLanguages.has(`${promoId}:en`)) {
      try {
        const fallbackResponse = await cdnClient.get(`/promo/${promoId}/en.json`, {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        i18n.addResourceBundle("en", "translation", fallbackResponse.data);
        fetchedLanguages.add(`${promoId}:en`);
      } catch (fallbackErr) {
        console.warn(`couldn't find resources for fallback language (en)`);
      }
    }
  }
};

// Only English is bundled, so it renders with no round trip and doubles as the
// fallbackLng. The other 37 locales are emitted as static files by the
// emit-locales plugin in vite.config.ts and fetched on demand.
const resources = {
  en: {
    translation: translationEN,
  },
};

const urlLng = resolveLanguage(ConfigService.getLng());

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    partialBundledLanguages: true,
    supportedLngs: supportedLngs,
    ...(urlLng ? { lng: urlLng } : {}),
    fallbackLng: "en",
    backend: {
      loadPath: `${import.meta.env.BASE_URL}locales/{{lng}}.json`,
    },
    detection: {
      order: ["querystring", "path", "cookie", "htmlTag"],
      caches: ["cookie"],
    },
    interpolation: {
      escapeValue: false,
    },
    // The app has no root Suspense boundary, so translations resolve by
    // re-render against the bundled English fallback instead of suspending.
    react: {
      useSuspense: false,
    },
  })
  .then(() => {
    fetchRemoteTranslationFiles(i18n.language);
  });

i18n.on("languageChanged", (lang) => {
  fetchRemoteTranslationFiles(resolveLanguage(lang));
});

export default i18n;
